import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { redeemProjectInvite } from '../../lib/apiClient';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function JoinPage() {
  const query = useQuery();
  const navigate = useNavigate();
  const { authStatus } = useAuth();

  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [error, setError] = useState('');

  useEffect(() => {
    const token = query.get('token');
    if (!token) {
      setStatus('error');
      setError('Missing invite token. Please check your invite link.');
      return;
    }

    if (authStatus === 'loading') return;

    if (authStatus === 'unauthenticated') {
      setStatus('error');
      setError('You need to sign in to accept this invite. Please log in, then click the invite link again.');
      return;
    }

    async function redeem() {
      try {
        setStatus('loading');
        setError('');
        const res = await redeemProjectInvite(token);
        const data = res?.data || res;
        if (!data?.project) {
          throw new Error('Invalid response from server');
        }
        setStatus('success');
        const slug = data.project.slug;
        if (slug) {
          navigate(`/app/brand/${slug}/dashboard`, { replace: true });
        }
      } catch (e) {
        console.error('Failed to redeem invite', e);
        setStatus('error');
        setError(e.message || 'Failed to accept invite');
      }
    }

    redeem();
  }, [authStatus, navigate, query]);

  return (
    <div className="min-h-screen bg-background text-white flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-surface border border-white/10 rounded-2xl p-6 text-center space-y-4">
        <h1 className="text-xl font-bold">Join Brand Workspace</h1>
        {status === 'loading' && (
          <p className="text-sm text-slate-400">Accepting your inviteplease wait</p>
        )}
        {status === 'success' && (
          <p className="text-sm text-slate-400">You have been added to the brand. Redirecting</p>
        )}
        {status === 'error' && (
          <p className="text-sm text-red-400 whitespace-pre-line">{error}</p>
        )}
      </div>
    </div>
  );
}
